import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Virtual Tour Embed (AMC Homestead)
//
// Two toggles — "Tour Your New Community" and "Tour Your New Home" — split the
// property's tours into community vs. apartment groups. Desktop uses tabs;
// mobile uses a full-width filter dropdown (matches Multihub Duda widget).
// Each tour opens in a modal. Data comes from the `virtual_tours` collection.
// ============================================================================

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
  virtual_tour_type?: string | null;
  virtual_tour_thumbnail?: string | null;
}

type TabKey = 'community' | 'apartment';

const TAB_LABELS: Record<TabKey, string> = {
  community: 'Tour Your New Community',
  apartment: 'Tour Your New Home',
};

const MOBILE_BREAKPOINT = 768;

/**
 * Returns true only if `value` looks like a real URL/path. Upstream feeds
 * sometimes pass literal strings like "false", "False", "null", "none",
 * etc. instead of an empty value — those should be treated as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

function classifyTour(tour: VirtualTour): TabKey {
  const typeVal = (tour.virtual_tour_type || '').toLowerCase();
  if (typeVal.includes('apartment')) return 'apartment';
  if (typeVal.includes('community')) return 'community';

  const title = (tour.virtual_tour_title || '').toLowerCase();
  if (title.includes('apartment') || title.includes('unit') || title.includes('home') || title.includes('floorplan'))
    return 'apartment';
  return 'community';
}

/** Matterport needs play=1 so mobile embeds start inline instead of popping a new tab. */
function getEmbedUrl(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    if (!parsed.hostname.toLowerCase().includes('matterport.com')) return rawUrl;
    parsed.searchParams.set('play', '1');
    parsed.searchParams.set('nt', '0');
    return parsed.href;
  } catch {
    return rawUrl;
  }
}

function TourRow({ tour, onOpen }: { tour: VirtualTour; onOpen: (tour: VirtualTour) => void }) {
  const hasThumbnail = isUsableUrl(tour.virtual_tour_thumbnail);
  const title = tour.virtual_tour_title || 'Virtual Tour';

  return (
    <button type="button" className="vt-row" onClick={() => onOpen(tour)} aria-label={`View ${title}`}>
      <div className={`vt-row-thumb ${hasThumbnail ? 'has-image' : ''}`}>
        {hasThumbnail && (
          <VibesitesImage url={tour.virtual_tour_thumbnail!} alt={title} className="w-full h-full object-cover" sizes="(max-width: 767px) 90px, 140px" />
        )}
      </div>

      <div className="vt-row-info">
        <p className="vt-row-title">{title}</p>
        <p className="vt-row-label">Virtual Tour</p>
      </div>

      <span className="vt-view-btn" aria-hidden="true">
        View Virtual Tour
      </span>
    </button>
  );
}

function TourModal({ tour, onClose }: { tour: VirtualTour; onClose: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const embedUrl = getEmbedUrl(tour.virtual_tour_url || '');

  return (
    <div className="vt-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label={tour.virtual_tour_title || 'Virtual Tour'}>
      <div className="vt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vt-modal-header">
          <p className="vt-modal-title">
            {tour.virtual_tour_title || 'Virtual Tour'}
          </p>
          <button type="button" onClick={onClose} className="vt-modal-close" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="vt-modal-body">
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            allowFullScreen
            allow="xr-spatial-tracking; gyroscope; accelerometer"
            title={tour.virtual_tour_title || 'Virtual Tour'}
          />
        </div>
      </div>
    </div>
  );
}

function MobileFilter({
  availableTabs,
  effectiveTab,
  onSelect,
}: {
  availableTabs: TabKey[];
  effectiveTab: TabKey;
  onSelect: (key: TabKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  return (
    <div className="vt-filter-dropdown" ref={rootRef}>
      <button
        type="button"
        className="vt-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{TAB_LABELS[effectiveTab]}</span>
        <span className="vt-filter-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="vt-filter-menu" role="listbox">
          {availableTabs.map((key) => (
            <button
              key={key}
              type="button"
              role="option"
              aria-selected={key === effectiveTab}
              className={`vt-filter-option ${key === effectiveTab ? 'active' : ''}`}
              onClick={() => {
                onSelect(key);
                setOpen(false);
              }}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const validTours = useMemo(
    () => (tours || []).filter((t: VirtualTour) => isUsableUrl(t.virtual_tour_url)),
    [tours],
  );

  const grouped = useMemo(() => {
    const map: Record<TabKey, VirtualTour[]> = { community: [], apartment: [] };
    for (const tour of validTours) {
      map[classifyTour(tour)].push(tour);
    }
    return map;
  }, [validTours]);

  const availableTabs = useMemo(
    () => (['community', 'apartment'] as TabKey[]).filter((k) => grouped[k].length > 0),
    [grouped],
  );

  const [activeTab, setActiveTab] = useState<TabKey>('community');
  const [modalTour, setModalTour] = useState<VirtualTour | null>(null);

  const openModal = useCallback((tour: VirtualTour) => setModalTour(tour), []);
  const closeModal = useCallback(() => setModalTour(null), []);

  useRevealStyles();
  const listRef = useScrollReveal<HTMLDivElement>();

  const effectiveTab = availableTabs.includes(activeTab)
    ? activeTab
    : availableTabs[0] || 'community';
  const activeTours = grouped[effectiveTab] || [];
  const showFilter = availableTabs.length > 1;

  if (validTours.length === 0) {
    return (
      <section className="pt-2 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-2 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {showFilter && (
          <>
            <div className="vt-tab-row">
              {availableTabs.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  aria-pressed={effectiveTab === key}
                  className={`vt-tab ${effectiveTab === key ? 'vt-tab--active' : ''}`}
                >
                  {TAB_LABELS[key]}
                </button>
              ))}
            </div>
            <MobileFilter
              availableTabs={availableTabs}
              effectiveTab={effectiveTab}
              onSelect={setActiveTab}
            />
          </>
        )}

        <div
          ref={listRef}
          className={`reveal-fade-up vt-list ${!showFilter ? 'vt-list--no-tabs' : ''}`}
        >
          {activeTours.map((tour, i) => (
            <TourRow key={`${effectiveTab}-${i}`} tour={tour} onOpen={openModal} />
          ))}
        </div>
      </div>

      {modalTour && <TourModal tour={modalTour} onClose={closeModal} />}

      <style>{`
        /* ── Tab bar (desktop) ── */
        .vt-tab-row {
          display: flex;
          gap: 0;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          margin-bottom: 0;
        }
        .vt-tab {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 500;
          letter-spacing: 0.02em;
          padding: 0.85rem 1.5rem;
          color: var(--vibe-color-text-muted, #6b7280);
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease;
          white-space: nowrap;
        }
        .vt-tab:hover {
          color: var(--vibe-color-text, #111);
        }
        .vt-tab--active {
          color: var(--vibe-color-text, #111);
          border-bottom-color: var(--vibe-color-text, #111);
          font-weight: 600;
        }

        /* ── Mobile filter dropdown (hidden on desktop) ── */
        .vt-filter-dropdown {
          display: none;
          position: relative;
          margin-bottom: 0.75rem;
        }
        .vt-filter-trigger {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.85rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-weight: 500;
          color: #fff;
          background: #000;
          border: 1px solid #000;
          border-radius: 3px;
          cursor: pointer;
          text-align: left;
        }
        .vt-filter-chevron {
          display: flex;
          align-items: center;
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }
        .vt-filter-chevron svg {
          width: 18px;
          height: 18px;
        }
        .vt-filter-trigger[aria-expanded="true"] .vt-filter-chevron {
          transform: rotate(180deg);
        }
        .vt-filter-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: 3px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 20;
          overflow: hidden;
        }
        .vt-filter-option {
          display: block;
          width: 100%;
          padding: 0.75rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-weight: 500;
          color: var(--vibe-color-text, #111);
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
        }
        .vt-filter-option:not(:last-child) {
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        .vt-filter-option.active {
          font-weight: 600;
          background: rgba(0, 0, 0, 0.04);
        }

        /* ── Tour list ── */
        .vt-list {
          display: flex;
          flex-direction: column;
        }
        .vt-list--no-tabs {
          border-top: 1px solid var(--vibe-color-border, #e5e7eb);
        }

        /* ── Tour row ── */
        .vt-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 1.25rem 0;
          border: none;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          background: none;
          cursor: pointer;
          text-align: left;
          font: inherit;
          color: inherit;
          -webkit-tap-highlight-color: transparent;
        }
        .vt-row:last-child {
          border-bottom: none;
        }
        .vt-row:hover .vt-row-thumb {
          opacity: 0.9;
        }
        .vt-row:hover .vt-view-btn {
          background: #fff;
          color: #000;
        }

        @media (min-width: ${MOBILE_BREAKPOINT}px) {
          .vt-row { gap: 1.5rem; }
        }

        .vt-row-info {
          flex: 1;
          min-width: 0;
          padding: 0.25rem 0;
        }
        .vt-row-title {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 1rem;
          line-height: 1.3;
          color: var(--vibe-color-text, #111);
          margin: 0;
        }
        .vt-row-label {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 700;
          color: var(--vibe-color-text, #111);
          margin: 0.25rem 0 0;
        }

        /* ── Thumbnail ── */
        .vt-row-thumb {
          flex-shrink: 0;
          width: 140px;
          height: 90px;
          border-radius: 4px;
          overflow: hidden;
          background: var(--vibe-color-surface, #f5f5f5);
        }

        /* ── View button (desktop) ── */
        .vt-view-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.7rem 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          text-decoration: none;
          color: #fff;
          background: #000;
          border: 1px solid #000;
          border-radius: 4px;
          transition: all 0.25s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── Modal ── */
        .vt-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.9);
          animation: vtFadeIn 0.2s ease;
        }

        .vt-modal {
          position: relative;
          width: min(95vw, 1920px);
          height: 90vh;
          height: 90dvh;
          background: #000;
          border-radius: 6px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: vtSlideUp 0.25s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .vt-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          min-height: 56px;
          padding: 0 0.5rem 0 1.25rem;
          background: #000;
          flex-shrink: 0;
        }

        .vt-modal-title {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 500;
          color: #fff;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding-right: 0.5rem;
        }

        .vt-modal-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.12);
          border: none;
          color: #fff;
          cursor: pointer;
          transition: background 0.2s ease;
          flex-shrink: 0;
          touch-action: manipulation;
        }
        .vt-modal-close:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        .vt-modal-body {
          position: relative;
          flex: 1;
          min-height: 0;
          background: #000;
        }
        .vt-modal-body iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        @keyframes vtFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes vtSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* ── Mobile (< 768px) — matches Multihub Duda widget ── */
        @media (max-width: ${MOBILE_BREAKPOINT - 1}px) {
          .vt-tab-row {
            display: none !important;
          }
          .vt-filter-dropdown {
            display: block;
          }

          .vt-row {
            gap: 1rem;
            padding: 0.75rem 0;
          }
          .vt-row-thumb {
            width: 90px;
            height: auto;
            aspect-ratio: 4 / 3;
          }
          .vt-row-title {
            font-size: 1.125rem;
            font-weight: 300;
          }
          .vt-row-label {
            font-size: 0.875rem;
            font-weight: 600;
          }
          .vt-view-btn {
            display: none;
          }

          .vt-modal {
            width: 100%;
            height: 100%;
            border-radius: 0;
          }
          .vt-modal-header {
            min-height: calc(52px + env(safe-area-inset-top, 0px));
            padding-top: env(safe-area-inset-top, 0px);
          }
          .vt-modal-body {
            padding-bottom: env(safe-area-inset-bottom, 0px);
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
